import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  Switch,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import {
  defaultWorkspaceApiClient,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { useService } from '@toeverything/infra';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getToggleState, MixedSwitch } from '../../shared/grouped-toggle-list';
import {
  SettingsEntityList,
  SettingsExpandableItem,
} from '../shared/settings-entity';

type AssistantSurface = 'all' | 'copilot' | 'mcp' | 'telegram';
type AssistantPolicyMode =
  | 'allow'
  | 'allow_temporary_grant'
  | 'allow_user_auto'
  | 'deny'
  | 'require_confirmation';
type AssistantSubjectType = 'group' | 'user' | 'workspace';

interface AssistantChannelRule {
  channel: 'telegram';
  enabled: boolean;
  subjectId: string;
  subjectType: AssistantSubjectType;
}

interface AssistantPolicy {
  capability: string;
  maxGrantSeconds: number | null;
  mode: AssistantPolicyMode;
  subjectId: string;
  subjectType: AssistantSubjectType;
  surface: AssistantSurface;
}

interface AssistantTelegramBotStatus {
  botConfigured: boolean;
  botTokenMasked: string | null;
  botUsername: string | null;
  lastError: string | null;
  lastVerifiedAt: string | null;
  status: string | null;
}

interface WorkspaceGroupOption {
  id: string;
  name: string;
}

interface WorkspaceMemberOption {
  avatarUrl?: string | null;
  email: string;
  id: string;
  name: string;
}

const CAPABILITIES = [
  'automation.delete',
  'automation.execute',
  'automation.read',
  'automation.write',
  'canvas.delete',
  'canvas.read',
  'canvas.write',
  'database.delete',
  'database.read',
  'database.write',
  'document.delete',
  'document.read',
  'document.write',
  'document.share',
  'image.generate',
  'people.invite',
  'people.read',
  'custom_app.write',
  'utility.read',
  'web.read',
  'workspace.write',
] as const;

type AssistantCapability = (typeof CAPABILITIES)[number];

const SURFACES: AssistantSurface[] = ['all', 'copilot', 'mcp', 'telegram'];
const POLICY_MODES: AssistantPolicyMode[] = [
  'allow',
  'allow_user_auto',
  'allow_temporary_grant',
  'require_confirmation',
  'deny',
];
const ENABLED_POLICY_MODES: AssistantPolicyMode[] = POLICY_MODES.filter(
  mode => mode !== 'deny'
);
const DEFAULT_ENABLED_POLICY_MODE: AssistantPolicyMode = 'allow_user_auto';

const CAPABILITY_GROUPS: Array<{
  key: string;
  i18nKey: string;
  capabilities: AssistantCapability[];
}> = [
  {
    key: 'document',
    i18nKey: 'com.streamlain.settings.workspace.ai.assistant.group-document',
    capabilities: [
      'document.read',
      'document.write',
      'document.share',
      'document.delete',
    ],
  },
  {
    key: 'database',
    i18nKey: 'com.streamlain.settings.workspace.ai.assistant.group-database',
    capabilities: ['database.read', 'database.write', 'database.delete'],
  },
  {
    key: 'automation',
    i18nKey: 'com.streamlain.settings.workspace.ai.assistant.group-automation',
    capabilities: [
      'automation.read',
      'automation.execute',
      'automation.write',
      'automation.delete',
    ],
  },
  {
    key: 'canvas-media',
    i18nKey:
      'com.streamlain.settings.workspace.ai.assistant.group-canvas-media',
    capabilities: [
      'canvas.read',
      'canvas.write',
      'canvas.delete',
      'image.generate',
      'web.read',
    ],
  },
  {
    key: 'workspace',
    i18nKey: 'com.streamlain.settings.workspace.ai.assistant.group-workspace',
    capabilities: [
      'people.read',
      'people.invite',
      'custom_app.write',
      'workspace.write',
      'utility.read',
    ],
  },
];

const compactControlStyle: CSSProperties = {
  minWidth: 150,
  padding: '6px 8px',
  borderRadius: 4,
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: 13,
} as const;

const compactTriggerStyle: CSSProperties = {
  width: '100%',
  minWidth: 150,
  fontWeight: 500,
};

const inlineFieldGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 8,
  alignItems: 'center',
};

const fullWidthFieldStyle: CSSProperties = {
  minWidth: 0,
};

const formRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  alignItems: 'center',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 6,
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: 12,
  lineHeight: '16px',
};

const telegramSetupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginBottom: 18,
};

const telegramTokenControlHeight = 40;

const helperCardStyle: CSSProperties = {
  padding: 14,
  borderRadius: 8,
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-hover-color)',
};

interface SettingsMenuOption {
  label: ReactNode;
  value: string;
}

const SettingsMenuSelect = ({
  value,
  options,
  onChange,
  disabled,
  placeholder,
  style,
}: {
  value: string;
  options: SettingsMenuOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: ReactNode;
  style?: CSSProperties;
}) => {
  const selected = options.find(option => option.value === value);

  return (
    <Menu
      items={options.map(option => (
        <MenuItem
          key={option.value}
          selected={option.value === value}
          onSelect={() => onChange(option.value)}
        >
          {option.label}
        </MenuItem>
      ))}
      contentOptions={{ align: 'start', style: { minWidth: 220 } }}
    >
      <MenuTrigger
        block
        disabled={disabled}
        style={{ ...compactTriggerStyle, ...style }}
      >
        {selected?.label ?? placeholder ?? '—'}
      </MenuTrigger>
    </Menu>
  );
};

export const AssistantPolicySettings = ({
  canEdit,
  showTelegram = true,
}: {
  canEdit: boolean;
  showTelegram?: boolean;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspace.id;
  const [rules, setRules] = useState<AssistantChannelRule[]>([]);
  const [policies, setPolicies] = useState<AssistantPolicy[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroupOption[]>([]);
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [telegramBot, setTelegramBot] =
    useState<AssistantTelegramBotStatus | null>(null);
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [isEditingTelegramBotToken, setIsEditingTelegramBotToken] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dedicatedSubjectType, setDedicatedSubjectType] =
    useState<AssistantSubjectType>('user');
  const [dedicatedSubjectId, setDedicatedSubjectId] = useState('');
  const [policySubjectType, setPolicySubjectType] =
    useState<AssistantSubjectType>('workspace');
  const [policySubjectId, setPolicySubjectId] = useState('');
  const [surface, setSurface] = useState<AssistantSurface>('all');
  const [policyMode, setPolicyMode] = useState<AssistantPolicyMode>(
    'allow_temporary_grant'
  );
  const [grantMinutes, setGrantMinutes] = useState(15);
  const [telegramAccessExpanded, setTelegramAccessExpanded] = useState(false);
  const [policyEditorExpanded, setPolicyEditorExpanded] = useState(false);
  const [expandedPolicyGroups, setExpandedPolicyGroups] = useState<Set<string>>(
    () => new Set()
  );
  const requestAssistant = useCallback(
    (
      path: string,
      init?: RequestInit,
      mode: 'background' | 'foreground' = 'background'
    ) =>
      defaultWorkspaceApiClient.request({
        workspaceId,
        path,
        init,
        category: 'settings',
        key: `${init?.method ?? 'GET'}:${path}`,
        mode,
        priority: mode === 'foreground' ? 'foreground' : 'visible',
      }),
    [workspaceId]
  );

  const workspaceTelegramEnabled = useMemo(() => {
    const workspaceRule = rules.find(rule => rule.subjectType === 'workspace');
    return workspaceRule?.enabled ?? true;
  }, [rules]);

  const dedicatedRules = useMemo(
    () => rules.filter(rule => rule.subjectType !== 'workspace'),
    [rules]
  );

  const subjectTypeLabel = useCallback(
    (subjectType: AssistantSubjectType) => {
      switch (subjectType) {
        case 'workspace':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.subject-workspace'
            ]?.() ?? 'Workspace'
          );
        case 'group':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.subject-group'
            ]?.() ?? 'Group'
          );
        case 'user':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.subject-user'
            ]?.() ?? 'User'
          );
      }
    },
    [t]
  );

  const surfaceLabel = useCallback(
    (item: AssistantSurface) => {
      switch (item) {
        case 'all':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.surface-all'
            ]?.() ?? 'All channels (general limit)'
          );
        case 'copilot':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.surface-copilot'
            ]?.() ?? 'Copilot only'
          );
        case 'mcp':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.surface-mcp'
            ]?.() ?? 'MCP only'
          );
        case 'telegram':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.surface-telegram'
            ]?.() ?? 'Telegram only'
          );
      }
    },
    [t]
  );

  const modeLabel = useCallback(
    (item: AssistantPolicyMode) => {
      switch (item) {
        case 'allow':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.mode-allow'
            ]?.() ?? 'Always allow'
          );
        case 'allow_user_auto':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.mode-allow_user_auto'
            ]?.() ?? 'User may enable auto approval'
          );
        case 'allow_temporary_grant':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.mode-allow_temporary_grant'
            ]?.() ?? 'Temporary grant'
          );
        case 'require_confirmation':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.mode-require_confirmation'
            ]?.() ?? 'Confirmation required'
          );
        case 'deny':
          return (
            t['com.streamlain.settings.workspace.ai.assistant.mode-deny']?.() ??
            'Deny'
          );
      }
    },
    [t]
  );

  const capabilityLabel = useCallback(
    (item: string) => {
      switch (item) {
        case 'document.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-document-read'
            ]?.() ?? 'Read documents'
          );
        case 'document.write':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-document-write'
            ]?.() ?? 'Write documents'
          );
        case 'document.share':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-document-share'
            ]?.() ?? 'Share documents'
          );
        case 'document.delete':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-document-delete'
            ]?.() ?? 'Delete documents'
          );
        case 'automation.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-automation-read'
            ]?.() ?? 'Read automations'
          );
        case 'automation.delete':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-automation-delete'
            ]?.() ?? 'Delete automations'
          );
        case 'canvas.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-canvas-read'
            ]?.() ?? 'Read canvas'
          );
        case 'canvas.delete':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-canvas-delete'
            ]?.() ?? 'Delete canvas elements'
          );
        case 'people.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-people-read'
            ]?.() ?? 'Read people'
          );
        case 'utility.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-utility-read'
            ]?.() ?? 'Use utility tools'
          );
        case 'database.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-database-read'
            ]?.() ?? 'Read databases'
          );
        case 'database.write':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-database-write'
            ]?.() ?? 'Edit databases'
          );
        case 'database.delete':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-database-delete'
            ]?.() ?? 'Delete databases'
          );
        case 'canvas.write':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-canvas-write'
            ]?.() ?? 'Edit canvas'
          );
        case 'automation.execute':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-automation-execute'
            ]?.() ?? 'Run automations'
          );
        case 'automation.write':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-automation-write'
            ]?.() ?? 'Edit automations'
          );
        case 'people.invite':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-people-invite'
            ]?.() ?? 'Invite people'
          );
        case 'custom_app.write':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-custom_app-write'
            ]?.() ?? 'Edit custom apps'
          );
        case 'web.read':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-web-read'
            ]?.() ?? 'Read web'
          );
        case 'image.generate':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-image-generate'
            ]?.() ?? 'Generate images'
          );
        case 'workspace.write':
          return (
            t[
              'com.streamlain.settings.workspace.ai.assistant.capability-workspace-write'
            ]?.() ?? 'Manage workspace'
          );
        default:
          return item;
      }
    },
    [t]
  );

  const subjectLabel = useCallback(
    (subjectType: AssistantSubjectType, subjectId: string) => {
      if (subjectType === 'workspace') return subjectTypeLabel('workspace');
      if (subjectType === 'group') {
        return groups.find(group => group.id === subjectId)?.name ?? subjectId;
      }
      const member = members.find(member => member.id === subjectId);
      return member
        ? `${member.name || member.email || member.id}${
            member.email ? ` (${member.email})` : ''
          }`
        : subjectId;
    },
    [groups, members, subjectTypeLabel]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [accessRes, policyRes, botRes, groupsRes, membersResult] =
        await Promise.all([
          requestAssistant(
            `/api/workspace/${workspaceId}/assistant/admin/channels/telegram/access`,
            undefined
          ),
          requestAssistant(
            `/api/workspace/${workspaceId}/assistant/admin/policies`
          ),
          requestAssistant(
            `/api/workspace/${workspaceId}/assistant/admin/telegram/bot`
          ),
          requestAssistant(`/api/workspace/${workspaceId}/groups`),
          serverService.server
            ? serverService.server.gql({
                query: getMembersByWorkspaceIdQuery,
                variables: {
                  workspaceId,
                  take: 100,
                },
              })
            : Promise.resolve(null),
        ]);
      if (accessRes.ok) {
        const data = await accessRes.json();
        setRules(data.rules ?? []);
      }
      if (policyRes.ok) {
        const data = await policyRes.json();
        setPolicies(data.policies ?? []);
      }
      if (botRes.ok) {
        setTelegramBot((await botRes.json()) as AssistantTelegramBotStatus);
      }
      if (groupsRes.ok) {
        setGroups((await groupsRes.json()) as WorkspaceGroupOption[]);
      }
      setMembers(
        (membersResult?.workspace?.members ?? []).map(member => ({
          id: member.id,
          name: member.name ?? '',
          email: member.email ?? '',
          avatarUrl: member.avatarUrl ?? null,
        }))
      );
    } finally {
      setIsLoading(false);
    }
  }, [requestAssistant, serverService.server, workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveChannelRule = useCallback(
    async (body: {
      subjectType: AssistantSubjectType;
      subjectId?: string;
      enabled: boolean;
    }) => {
      setIsSaving(true);
      try {
        const res = await requestAssistant(
          `/api/workspace/${workspaceId}/assistant/admin/channels/access`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ channel: 'telegram', ...body }),
          },
          'foreground'
        );
        if (!res.ok) throw new Error('Failed to update channel access');
        await load();
      } catch {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.ai.assistant.save-failed'
            ]?.() ?? 'Assistant settings could not be saved',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [load, requestAssistant, t, workspaceId]
  );

  const saveTelegramBot = useCallback(async () => {
    if (!telegramBotToken.trim()) return;
    setIsSaving(true);
    try {
      const res = await requestAssistant(
        `/api/workspace/${workspaceId}/assistant/admin/telegram/bot`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ botToken: telegramBotToken.trim() }),
        },
        'foreground'
      );
      if (!res.ok) throw new Error('Failed to configure Telegram bot');
      setTelegramBotToken('');
      setIsEditingTelegramBotToken(false);
      await load();
    } catch {
      notify.error({
        title:
          t['com.streamlain.settings.workspace.ai.assistant.save-failed']?.() ??
          'Assistant settings could not be saved',
      });
    } finally {
      setIsSaving(false);
    }
  }, [load, requestAssistant, t, telegramBotToken, workspaceId]);

  const disconnectTelegramBot = useCallback(async () => {
    setIsSaving(true);
    try {
      const res = await requestAssistant(
        `/api/workspace/${workspaceId}/assistant/admin/telegram/bot`,
        { method: 'DELETE' },
        'foreground'
      );
      if (!res.ok) throw new Error('Failed to disconnect Telegram bot');
      setTelegramBotToken('');
      setIsEditingTelegramBotToken(false);
      await load();
    } catch {
      notify.error({
        title:
          t['com.streamlain.settings.workspace.ai.assistant.save-failed']?.() ??
          'Assistant settings could not be saved',
      });
    } finally {
      setIsSaving(false);
    }
  }, [load, requestAssistant, t, workspaceId]);

  const selectedPolicySubjectId =
    policySubjectType === 'workspace' ? 'workspace' : policySubjectId.trim();
  const canEditSelectedPolicySubject =
    policySubjectType === 'workspace' || Boolean(policySubjectId.trim());

  const selectedPoliciesByCapability = useMemo(() => {
    const entries = policies
      .filter(
        policy =>
          policy.subjectType === policySubjectType &&
          policy.subjectId === selectedPolicySubjectId &&
          policy.surface === surface
      )
      .map(policy => [policy.capability, policy] as const);
    return new Map<string, AssistantPolicy>(entries);
  }, [policies, policySubjectType, selectedPolicySubjectId, surface]);

  const getCapabilityMode = useCallback(
    (item: AssistantCapability): AssistantPolicyMode =>
      selectedPoliciesByCapability.get(item)?.mode ??
      DEFAULT_ENABLED_POLICY_MODE,
    [selectedPoliciesByCapability]
  );

  const isCapabilityEnabled = useCallback(
    (item: AssistantCapability): boolean => getCapabilityMode(item) !== 'deny',
    [getCapabilityMode]
  );

  const savePolicyModes = useCallback(
    async (
      capabilities: AssistantCapability[],
      nextMode: AssistantPolicyMode
    ) => {
      if (!canEditSelectedPolicySubject) return;
      setIsSaving(true);
      try {
        await Promise.all(
          capabilities.map(async item => {
            const res = await requestAssistant(
              `/api/workspace/${workspaceId}/assistant/admin/policies`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  subjectType: policySubjectType,
                  subjectId:
                    policySubjectType === 'workspace'
                      ? undefined
                      : policySubjectId.trim(),
                  surface,
                  capability: item,
                  mode: nextMode,
                  maxGrantSeconds:
                    nextMode === 'allow_temporary_grant'
                      ? Math.max(1, Math.min(24 * 60, grantMinutes)) * 60
                      : null,
                }),
              },
              'foreground'
            );
            if (!res.ok) throw new Error('Failed to update assistant policy');
          })
        );
        await load();
      } catch {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.ai.assistant.save-failed'
            ]?.() ?? 'Assistant settings could not be saved',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      canEditSelectedPolicySubject,
      grantMinutes,
      load,
      policySubjectId,
      policySubjectType,
      requestAssistant,
      surface,
      t,
      workspaceId,
    ]
  );

  const addDedicatedRule = useCallback(() => {
    if (!dedicatedSubjectId.trim()) return;
    void saveChannelRule({
      subjectType: dedicatedSubjectType,
      subjectId: dedicatedSubjectId.trim(),
      enabled: true,
    }).then(() => setDedicatedSubjectId(''));
  }, [dedicatedSubjectId, dedicatedSubjectType, saveChannelRule]);

  const subjectTypeOptions = useMemo<SettingsMenuOption[]>(
    () => [
      { value: 'user', label: subjectTypeLabel('user') },
      { value: 'group', label: subjectTypeLabel('group') },
    ],
    [subjectTypeLabel]
  );

  const policySubjectTypeOptions = useMemo<SettingsMenuOption[]>(
    () => [
      { value: 'workspace', label: subjectTypeLabel('workspace') },
      { value: 'user', label: subjectTypeLabel('user') },
      { value: 'group', label: subjectTypeLabel('group') },
    ],
    [subjectTypeLabel]
  );

  const groupOptions = useMemo<SettingsMenuOption[]>(
    () => groups.map(group => ({ value: group.id, label: group.name })),
    [groups]
  );

  const memberOptions = useMemo<SettingsMenuOption[]>(
    () =>
      members.map(member => ({
        value: member.id,
        label: `${member.name || member.email || member.id}${
          member.email ? ` (${member.email})` : ''
        }`,
      })),
    [members]
  );

  const surfaceOptions = useMemo<SettingsMenuOption[]>(
    () => SURFACES.map(item => ({ value: item, label: surfaceLabel(item) })),
    [surfaceLabel]
  );

  const modeOptions = useMemo<SettingsMenuOption[]>(
    () =>
      ENABLED_POLICY_MODES.map(item => ({
        value: item,
        label: modeLabel(item),
      })),
    [modeLabel]
  );

  const selectSubjectPlaceholder =
    dedicatedSubjectType === 'group'
      ? (t['com.streamlain.settings.workspace.ai.assistant.group-select']?.() ??
        'Select group')
      : (t['com.streamlain.settings.workspace.ai.assistant.user-select']?.() ??
        'Select user');

  const selectPolicySubjectPlaceholder =
    policySubjectType === 'group'
      ? (t['com.streamlain.settings.workspace.ai.assistant.group-select']?.() ??
        'Select group')
      : (t['com.streamlain.settings.workspace.ai.assistant.user-select']?.() ??
        'Select user');

  const telegramBotConfigured = Boolean(telegramBot?.botConfigured);
  const telegramTokenReadonly =
    telegramBotConfigured && !isEditingTelegramBotToken;
  const telegramTokenInputValue = telegramTokenReadonly
    ? (telegramBot?.botTokenMasked ??
      t[
        'com.streamlain.settings.workspace.ai.assistant.telegram-bot-configured'
      ]?.() ??
      'Configured')
    : telegramBotToken;

  return (
    <>
      {showTelegram ? (
        <SettingWrapper
          title={
            t[
              'com.streamlain.settings.workspace.ai.assistant.telegram-section'
            ]?.() ?? 'Telegram'
          }
        >
          <SettingRow
            name={
              t[
                'com.streamlain.settings.workspace.ai.assistant.telegram-all'
              ]?.() ?? 'Telegram access'
            }
            desc={
              dedicatedRules.length > 0 && !workspaceTelegramEnabled
                ? (t[
                    'com.streamlain.settings.workspace.ai.assistant.telegram-mode-allowlist-desc'
                  ]?.() ??
                  'Dedicated rules are active. Only listed users or groups can connect Telegram.')
                : (t[
                    'com.streamlain.settings.workspace.ai.assistant.telegram-mode-all-desc'
                  ]?.() ?? 'Allow every workspace member to connect Telegram.')
            }
          >
            <Switch
              checked={workspaceTelegramEnabled}
              disabled={!canEdit || isLoading || isSaving}
              onChange={checked =>
                void saveChannelRule({
                  subjectType: 'workspace',
                  enabled: checked,
                })
              }
            />
          </SettingRow>

          {workspaceTelegramEnabled || dedicatedRules.length > 0 ? (
            <>
              <SettingRow
                name={
                  t[
                    'com.streamlain.settings.workspace.ai.assistant.telegram-bot'
                  ]?.() ?? 'Workspace Telegram bot'
                }
                desc={
                  telegramBot?.botConfigured
                    ? (t[
                        'com.streamlain.settings.workspace.ai.assistant.telegram-bot-connected'
                      ]?.({ username: telegramBot.botUsername ?? '' }) ??
                      `Connected to @${telegramBot.botUsername}`)
                    : (t[
                        'com.streamlain.settings.workspace.ai.assistant.telegram-bot-desc'
                      ]?.() ??
                      `Configure one Telegram bot for this workspace. Members connect their own private chat through ${BRAND_DISPLAY_NAME}.`)
                }
              >
                {telegramBot?.botConfigured ? (
                  <Button
                    variant="plain"
                    disabled={!canEdit || isLoading || isSaving}
                    onClick={() => void disconnectTelegramBot()}
                  >
                    {t[
                      'com.streamlain.settings.workspace.ai.assistant.telegram-bot-disconnect'
                    ]?.() ?? 'Disconnect'}
                  </Button>
                ) : null}
              </SettingRow>

              <div style={telegramSetupStyle}>
                <div style={helperCardStyle}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          color: 'var(--streamlain-text-primary-color)',
                          fontSize: 13,
                          fontWeight: 600,
                          lineHeight: '20px',
                        }}
                      >
                        {t[
                          'com.streamlain.settings.workspace.ai.assistant.telegram-bot-help-title'
                        ]?.() ?? 'How to get the Telegram bot token'}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          color: 'var(--streamlain-text-secondary-color)',
                          fontSize: 12,
                          lineHeight: '18px',
                        }}
                      >
                        {t[
                          'com.streamlain.settings.workspace.ai.assistant.telegram-bot-help-desc'
                        ]?.() ??
                          'Open BotFather in Telegram, send /newbot and follow the prompts. Paste the returned token here afterwards.'}
                      </div>
                    </div>
                    <Button
                      variant="plain"
                      disabled={!canEdit || isSaving}
                      onClick={() =>
                        window.open('https://t.me/BotFather', '_blank')
                      }
                    >
                      {t[
                        'com.streamlain.settings.workspace.ai.assistant.telegram-bot-open-botfather'
                      ]?.() ?? 'Open BotFather'}
                    </Button>
                  </div>
                  <div
                    style={{
                      marginTop: 10,
                      color: 'var(--streamlain-warning-color)',
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    {t[
                      'com.streamlain.settings.workspace.ai.assistant.telegram-bot-token-note'
                    ]?.() ??
                      'Treat this token like a password. Anyone with it can control the workspace bot.'}
                  </div>
                </div>

                <div
                  style={{
                    ...formRowStyle,
                    alignItems: 'stretch',
                  }}
                >
                  <input
                    type={telegramTokenReadonly ? 'text' : 'password'}
                    value={telegramTokenInputValue}
                    disabled={!canEdit || isSaving || telegramTokenReadonly}
                    readOnly={telegramTokenReadonly}
                    onChange={event => {
                      if (!telegramTokenReadonly) {
                        setTelegramBotToken(event.target.value);
                      }
                    }}
                    placeholder={
                      t[
                        'com.streamlain.settings.workspace.ai.assistant.telegram-bot-token-placeholder'
                      ]?.() ?? 'Telegram bot token from BotFather'
                    }
                    style={{
                      ...compactControlStyle,
                      flex: '1 1 320px',
                      height: telegramTokenControlHeight,
                      boxSizing: 'border-box',
                    }}
                  />
                  <Button
                    variant="primary"
                    disabled={
                      !canEdit ||
                      isSaving ||
                      (!telegramTokenReadonly && !telegramBotToken.trim())
                    }
                    onClick={() => {
                      if (telegramTokenReadonly) {
                        setIsEditingTelegramBotToken(true);
                        setTelegramBotToken('');
                        return;
                      }
                      void saveTelegramBot();
                    }}
                    style={{
                      height: telegramTokenControlHeight,
                      boxSizing: 'border-box',
                    }}
                  >
                    {telegramTokenReadonly
                      ? (t[
                          'com.streamlain.settings.workspace.ai.assistant.telegram-bot-replace'
                        ]?.() ?? 'Replace bot')
                      : telegramBotConfigured
                        ? (t[
                            'com.streamlain.settings.workspace.ai.assistant.telegram-bot-replace'
                          ]?.() ?? 'Replace bot')
                        : (t[
                            'com.streamlain.settings.workspace.ai.assistant.telegram-bot-save'
                          ]?.() ?? 'Save bot')}
                  </Button>
                </div>

                {telegramBot?.lastError ? (
                  <div
                    style={{
                      color: 'var(--streamlain-error-color)',
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    {telegramBot.lastError}
                  </div>
                ) : null}
              </div>

              <SettingsEntityList>
                <SettingsExpandableItem
                  title={
                    t[
                      'com.streamlain.settings.workspace.ai.assistant.telegram-access-rules'
                    ]?.() ?? 'Access rules'
                  }
                  meta={
                    dedicatedRules.length > 0
                      ? (t[
                          'com.streamlain.settings.workspace.ai.assistant.telegram-access-rules-summary'
                        ]?.({ count: String(dedicatedRules.length) }) ??
                        `${dedicatedRules.length} dedicated rules`)
                      : (t[
                          'com.streamlain.settings.workspace.ai.assistant.telegram-access-rules-empty'
                        ]?.() ?? 'No dedicated rules')
                  }
                  expanded={telegramAccessExpanded}
                  onToggle={() => setTelegramAccessExpanded(prev => !prev)}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 14,
                    }}
                  >
                    <div style={inlineFieldGridStyle}>
                      <div style={fullWidthFieldStyle}>
                        <SettingsMenuSelect
                          value={dedicatedSubjectType}
                          options={subjectTypeOptions}
                          disabled={!canEdit || isSaving}
                          onChange={value => {
                            setDedicatedSubjectType(
                              value as AssistantSubjectType
                            );
                            setDedicatedSubjectId('');
                          }}
                        />
                      </div>
                      <div style={fullWidthFieldStyle}>
                        <SettingsMenuSelect
                          value={dedicatedSubjectId}
                          options={
                            dedicatedSubjectType === 'group'
                              ? groupOptions
                              : memberOptions
                          }
                          placeholder={selectSubjectPlaceholder}
                          disabled={!canEdit || isSaving}
                          onChange={setDedicatedSubjectId}
                        />
                      </div>
                      <Button
                        variant="primary"
                        disabled={
                          !canEdit || !dedicatedSubjectId.trim() || isSaving
                        }
                        onClick={addDedicatedRule}
                      >
                        {t[
                          'com.streamlain.settings.workspace.ai.assistant.telegram-rule-add'
                        ]?.() ?? 'Add rule'}
                      </Button>
                    </div>

                    {dedicatedRules.map(rule => (
                      <SettingRow
                        key={`${rule.subjectType}:${rule.subjectId}`}
                        name={`${subjectTypeLabel(rule.subjectType)}: ${subjectLabel(
                          rule.subjectType,
                          rule.subjectId
                        )}`}
                        desc={
                          rule.enabled
                            ? (t[
                                'com.streamlain.settings.workspace.ai.assistant.telegram-rule-enabled-desc'
                              ]?.() ?? 'Telegram enabled by dedicated rule')
                            : (t[
                                'com.streamlain.settings.workspace.ai.assistant.telegram-rule-disabled-desc'
                              ]?.() ?? 'Telegram disabled by dedicated rule')
                        }
                      >
                        <Switch
                          checked={rule.enabled}
                          disabled={!canEdit || isSaving}
                          onChange={checked =>
                            void saveChannelRule({
                              subjectType: rule.subjectType,
                              subjectId: rule.subjectId,
                              enabled: checked,
                            })
                          }
                        />
                      </SettingRow>
                    ))}
                  </div>
                </SettingsExpandableItem>
              </SettingsEntityList>
            </>
          ) : null}
        </SettingWrapper>
      ) : null}

      <SettingWrapper
        title={
          t['com.streamlain.settings.workspace.ai.assistant.policies']?.() ??
          'Assistant action policies'
        }
      >
        <SettingsEntityList>
          <SettingsExpandableItem
            title={
              t[
                'com.streamlain.settings.workspace.ai.assistant.policy-add'
              ]?.() ?? 'General rules and channel restrictions'
            }
            meta={
              t[
                'com.streamlain.settings.workspace.ai.assistant.policy-add-desc'
              ]?.() ??
              'Set what assistant actions are allowed across all channels first. Then choose a channel to tighten Copilot, Telegram or MCP further; channel rules cannot loosen the general limit.'
            }
            expanded={policyEditorExpanded}
            onToggle={() => setPolicyEditorExpanded(prev => !prev)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={inlineFieldGridStyle}>
                <div style={fullWidthFieldStyle}>
                  <label style={fieldLabelStyle}>
                    {t[
                      'com.streamlain.settings.workspace.ai.assistant.policy-subject'
                    ]?.() ?? 'Scope'}
                  </label>
                  <SettingsMenuSelect
                    value={policySubjectType}
                    options={policySubjectTypeOptions}
                    disabled={!canEdit || isSaving}
                    onChange={value => {
                      setPolicySubjectType(value as AssistantSubjectType);
                      setPolicySubjectId('');
                    }}
                  />
                </div>
                {policySubjectType === 'group' ||
                policySubjectType === 'user' ? (
                  <div style={fullWidthFieldStyle}>
                    <label style={fieldLabelStyle}>
                      {selectPolicySubjectPlaceholder}
                    </label>
                    <SettingsMenuSelect
                      value={policySubjectId}
                      options={
                        policySubjectType === 'group'
                          ? groupOptions
                          : memberOptions
                      }
                      placeholder={selectPolicySubjectPlaceholder}
                      disabled={!canEdit || isSaving}
                      onChange={setPolicySubjectId}
                    />
                  </div>
                ) : null}
                <div style={fullWidthFieldStyle}>
                  <label style={fieldLabelStyle}>
                    {t[
                      'com.streamlain.settings.workspace.ai.assistant.policy-surface'
                    ]?.() ?? 'Applies to'}
                  </label>
                  <SettingsMenuSelect
                    value={surface}
                    options={surfaceOptions}
                    disabled={!canEdit || isSaving}
                    onChange={value => setSurface(value as AssistantSurface)}
                  />
                </div>
                <div style={fullWidthFieldStyle}>
                  <label style={fieldLabelStyle}>
                    {t[
                      'com.streamlain.settings.workspace.ai.assistant.policy-enabled-mode'
                    ]?.() ?? 'Mode for enabled actions'}
                  </label>
                  <SettingsMenuSelect
                    value={policyMode}
                    options={modeOptions}
                    disabled={!canEdit || isSaving}
                    onChange={value =>
                      setPolicyMode(value as AssistantPolicyMode)
                    }
                  />
                </div>
                {policyMode === 'allow_temporary_grant' ? (
                  <label
                    style={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 6,
                      minWidth: 150,
                      color: 'var(--streamlain-text-secondary-color)',
                      fontSize: 12,
                    }}
                  >
                    {t[
                      'com.streamlain.settings.workspace.ai.assistant.grant-minutes'
                    ]?.() ?? 'Grant minutes'}
                    <input
                      type="number"
                      min={1}
                      max={24 * 60}
                      value={grantMinutes}
                      disabled={!canEdit || isSaving}
                      onChange={event => {
                        const next = Number.parseInt(event.target.value, 10);
                        setGrantMinutes(Number.isFinite(next) ? next : 15);
                      }}
                      style={{
                        ...compactControlStyle,
                        width: '100%',
                        minWidth: 90,
                      }}
                    />
                  </label>
                ) : null}
              </div>

              <SettingsEntityList>
                {CAPABILITY_GROUPS.map(group => {
                  const groupState = getToggleState(
                    group.capabilities,
                    isCapabilityEnabled
                  );
                  const enabledCount =
                    group.capabilities.filter(isCapabilityEnabled).length;
                  const expanded = expandedPolicyGroups.has(group.key);
                  return (
                    <SettingsExpandableItem
                      key={group.key}
                      title={
                        t[group.i18nKey as keyof typeof t]?.() ?? group.key
                      }
                      meta={
                        t[
                          'com.streamlain.settings.workspace.ai.assistant.policy-group-summary'
                        ]?.({
                          enabled: String(enabledCount),
                          total: String(group.capabilities.length),
                        }) ??
                        `${enabledCount} of ${group.capabilities.length} allowed`
                      }
                      aside={
                        <MixedSwitch
                          state={groupState}
                          disabled={
                            !canEdit ||
                            isSaving ||
                            !canEditSelectedPolicySubject
                          }
                          onToggle={() =>
                            void savePolicyModes(
                              group.capabilities,
                              groupState === 'on' ? 'deny' : policyMode
                            )
                          }
                        />
                      }
                      expanded={expanded}
                      onToggle={() =>
                        setExpandedPolicyGroups(prev => {
                          const next = new Set(prev);
                          if (next.has(group.key)) next.delete(group.key);
                          else next.add(group.key);
                          return next;
                        })
                      }
                    >
                      {group.capabilities.map(item => {
                        const currentMode = getCapabilityMode(item);
                        const enabled = currentMode !== 'deny';
                        return (
                          <SettingRow
                            key={item}
                            name={capabilityLabel(item)}
                            desc={modeLabel(currentMode)}
                          >
                            <Switch
                              checked={enabled}
                              disabled={
                                !canEdit ||
                                isSaving ||
                                !canEditSelectedPolicySubject
                              }
                              onChange={checked =>
                                void savePolicyModes(
                                  [item],
                                  checked ? policyMode : 'deny'
                                )
                              }
                            />
                          </SettingRow>
                        );
                      })}
                    </SettingsExpandableItem>
                  );
                })}
              </SettingsEntityList>

              {!canEditSelectedPolicySubject ? (
                <div
                  style={{
                    color: 'var(--streamlain-text-secondary-color)',
                    fontSize: 12,
                    lineHeight: '18px',
                  }}
                >
                  {t[
                    'com.streamlain.settings.workspace.ai.assistant.policy-subject-required'
                  ]?.() ?? 'Select a user or group before editing policies.'}
                </div>
              ) : null}
            </div>
          </SettingsExpandableItem>
        </SettingsEntityList>

        {policies.map(policy => (
          <SettingRow
            key={`${policy.subjectType}:${policy.subjectId}:${policy.surface}:${policy.capability}`}
            name={`${subjectLabel(
              policy.subjectType,
              policy.subjectId
            )} / ${surfaceLabel(policy.surface)} / ${capabilityLabel(
              policy.capability
            )}`}
            desc={`${modeLabel(policy.mode)}${
              policy.maxGrantSeconds
                ? ` (${Math.round(policy.maxGrantSeconds / 60)} min)`
                : ''
            }`}
          />
        ))}
      </SettingWrapper>
    </>
  );
};
